// InputArea component for user input in the chat interface

import React, { useState, useRef, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import FeatureSelects from './FeatureSelects';
import ActionButtons from './actionButtons';
import { useMessageHandler } from '../contexts/MessageContext';
import { useChatSession } from '../contexts/ChatSessionContext.js';
import { transliterateWithMapping } from '../utils/transliteration';
import { UPLOAD_CONFIG, TEXT_LENGTH_THRESHOLD, MAX_ATTACHMENT_SIZE } from '../constants/appConstants';
import PlaybackControls from './PlaybackControls';
import RecordingIndicator from './RecordingIndicator';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { getTranslucentColor, getBackgroundColor } from '../themes';

// Add this styled component after the existing styled components
const DropZone = styled('div')(({ theme, isDragging, isValid }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: isDragging ? 'flex' : 'none',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: getTranslucentColor(theme, 'lg'),
  border: `2px dashed ${isValid ? theme.palette.primary.main : theme.palette.error.main}`,
  zIndex: 1000,
  pointerEvents: 'none'
}));

// Styled component for the input area container
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: getBackgroundColor(theme, 'input'),
  color: theme.palette.text.primary,
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
}));

// InputArea functional component
const InputArea = ({
  translit,
  selectedEngine,
  onEngineChange,
  selectedService,
  setSelectedService,
  isAuthenticated,
  onUpload,
  attachments,
  setAttachments,
  isHidden,
  autoOptimizePrompt,
  enableThinking,
  onMessageSent,
}) => {
  // State and hooks
  //const [message, setMessage] = useState('');
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const { setMessages,
    sendMessage,
    showDialog,
    optimizePrompt,
    loadingInputArea,
    isTranscribing,
    isPlaybackActive,
    playbackData,
    pausePlayback,
    resumePlayback,
    stopPlayback,
    seekPlayback,
    isProcessing,
    isRecording,
    currentSession,
    setCurrentSession,
    selectedSystemPrompt,
  } = useMessageHandler();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [isDragging, setIsDragging] = useState(false);
  const [isValidDrag, setIsValidDrag] = useState(false);

  const { settings } = useUserSettings();

  const { fetchChatSessions, projects } = useChatSession();

  // Find current project based on session
  const currentProject = projects?.find(project =>
    project.sessions?.includes(currentSession)
  );

  const isValidFileType = (file) => {
    return UPLOAD_CONFIG.ACCEPTED_TYPES.includes(file.type);
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData.items;
    const imageFiles = [];
    const pdfFiles = [];
    const textItems = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const newFile = new File([file], `pasted-image-${timestamp}.png`, {
          type: file.type,
        });
        imageFiles.push(newFile);
      } else if (item.type === 'application/pdf') {
        const file = item.getAsFile();
        pdfFiles.push(file);
      } else if (item.type === 'text/plain') {
        textItems.push(item);
      }
    }

    if (imageFiles.length > 0 || pdfFiles.length > 0) {
      onUpload([...imageFiles, ...pdfFiles]);
      event.preventDefault();
      return;
    }

    // Handle text paste
    if (textItems.length > 0) {
      event.preventDefault(); // Prevent default paste

      // Get the text content
      const text = event.clipboardData.getData('text/plain');

      // Check text length
      if (text.length > TEXT_LENGTH_THRESHOLD) {
        // Create timestamp for unique filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        // Check size
        const textSize = new Blob([text]).size;
        if (textSize > MAX_ATTACHMENT_SIZE) {
          alert(`Text is too large (${(textSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${MAX_ATTACHMENT_SIZE / 1024 / 1024}MB.`);
          return;
        }

        // Create text attachment
        const textAttachment = {
          originalName: `pasted-text-${timestamp}.txt`,
          content: text,
          type: 'text/plain',
          size: textSize
        };

        // Add to attachments
        setAttachments(prev => [...prev, textAttachment]);
      } else {
        // For shorter text, insert at cursor position
        const start = event.target.selectionStart;
        const end = event.target.selectionEnd;
        const currentValue = inputValue;
        const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
        setInputValue(newValue);

        // Set cursor position after pasted text
        setTimeout(() => {
          event.target.selectionStart = start + text.length;
          event.target.selectionEnd = start + text.length;
        }, 0);
      }
    }
  };

  const focusInput = () => {
    if (inputRef.current) {
      // First focus the input
      inputRef.current.focus();
      // Then force cursor activation by selecting at end of text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  };

  // Handler for sending a message
  const handleSend = async () => {
    if (!inputValue.trim() && (!attachments || attachments.length === 0)) return;

    if (selectedService === 'optimizePrompt') {

      showDialog(t('optimizePrompt.title'), t('optimizePrompt.content'), async () => {
        const optimizedPrompt = await optimizePrompt(inputValue, selectedEngine);
        if (optimizedPrompt) {
          setInputValue(optimizedPrompt);
          setSelectedService('');
          setTimeout(focusInput, 0);
        }
      });

      return;
    }

    // Save current input value and attachments before sending
    const savedInputValue = inputValue;
    const savedAttachments = [...attachments];

    // Separate files into images and text documents
    const fileAttachments = attachments.filter(file =>
      file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    const textFiles = attachments.filter(file =>
      !file.type.startsWith('image/') && file.type !== 'application/pdf'
    );

    let sessionId = currentSession;
    if (!sessionId) {
      sessionId = uuidv4();
      setCurrentSession(sessionId)
    }

    const formData = new FormData();
    formData.append('userMessage', inputValue);
    formData.append('autoOptimizePrompt', autoOptimizePrompt.toString());
    formData.append('enableThinking', enableThinking.toString());
    formData.append('selectedEngine', selectedEngine);
    formData.append('selectedService', selectedService);
    formData.append('sessionId', sessionId);

    // Add system prompt if selected
    if (selectedSystemPrompt) {
      const promptObj = settings.systemPrompts?.find(p => p.name === selectedSystemPrompt);
      if (promptObj) {
        formData.append('systemPrompt', promptObj.content);
        formData.append('systemPromptName', selectedSystemPrompt);
      }
    }

    // Append image files
    fileAttachments.forEach(attachment => {
      formData.append('files', attachment.file);
    });

    // Append text files as separate field
    if (textFiles.length > 0) {
      formData.append('textAttachments', JSON.stringify(textFiles.map(file => ({
        originalName: file.originalName,
        content: file.content,
        type: file.type
      }))));
    }

    // Add project ID to form data if session belongs to a project
    if (currentProject) {
      formData.append('projectId', currentProject.id);
    }

    try {
      setInputValue('');
      setAttachments([]);

      const result = await sendMessage(formData, setMessages, selectedEngine, selectedService);

      if (result && result.success) {
        await fetchChatSessions();
        setSelectedService('');
        setTimeout(focusInput, 0);
        // Call the callback to handle state changes after successful message send
        if (onMessageSent) {
          onMessageSent();
        }
      } else {
        // If there was an error, restore the input and attachments
        setInputValue(savedInputValue);
        setAttachments(savedAttachments);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Restore input value and attachments on error
      setInputValue(savedInputValue);
      setAttachments(savedAttachments);
    }

  };

  // Handler for key press events (Enter to send)
  const handleKeyPress = (event) => {

    if (event.key === 'Enter' || event.key === '\n') {

      if (isMobile || settings.ctrlEnterToSend) {
        // If Ctrl+Enter is required, only send if Ctrl is pressed
        if (event.ctrlKey) {
          event.preventDefault();
          handleSend();
        }
        // Otherwise allow the newline
      } else {
        // Original behavior - send on Enter unless Shift is pressed
        if (!event.shiftKey) {
          event.preventDefault();
          handleSend();
        }
      }
    }
  };

  const handleInputChange = (event) => {
    const inputElement = event.target;
    const caretPosition = inputElement.selectionStart;

    if (!translit || isMobile) {
      setInputValue(inputElement.value);
      return;
    }

    const { transliteratedText, newCaretPosition } = transliterateWithMapping(inputElement.value, caretPosition);

    setInputValue(transliteratedText);

    // Set the caret position
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCaretPosition, newCaretPosition);
      }
    }, 0);
  };

  const dragCounter = useRef(0);

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current++;

    // Check if the dragged items include valid files
    if (event.dataTransfer.items) {
      const isValid = Array.from(event.dataTransfer.items).some(item =>
        item.kind === 'file' && isValidFileType({ type: item.type })
      );
      setIsValidDrag(isValid);
    }

    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsValidDrag(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Update valid state during drag over
    if (event.dataTransfer.items) {
      const isValid = Array.from(event.dataTransfer.items).some(item =>
        item.kind === 'file' && isValidFileType({ type: item.type })
      );
      setIsValidDrag(isValid);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current = 0;
    setIsDragging(false);
    setIsValidDrag(false);

    const files = Array.from(event.dataTransfer.files)
      .filter(file => isValidFileType(file));

    if (files.length > 0) {
      onUpload(files);
    }
  };

  // Reset drag state when mouse leaves window
  useEffect(() => {
    const handleWindowDragLeave = (event) => {
      if (!event.relatedTarget) {
        dragCounter.current = 0;
        setIsDragging(false);
        setIsValidDrag(false);
      }
    };

    window.addEventListener('dragleave', handleWindowDragLeave);
    return () => window.removeEventListener('dragleave', handleWindowDragLeave);
  }, []);

  // Combined loading state
  const isLoading = loadingInputArea || isTranscribing;

  // Combined disabled state
  const isDisabled = isProcessing || isRecording || isPlaybackActive;

  useEffect(() => {
    if (!isProcessing && !isRecording) {
      setTimeout(focusInput, 0); // Use setTimeout to ensure proper timing
    }
  }, [isProcessing, isRecording]);

  return (
    <Item sx={{
      minHeight: isHidden ? '0' : '150px',
      flexGrow: 1,
      backgroundColor: getBackgroundColor(theme, 'input'),
      position: 'relative',
      visibility: isHidden ? 'hidden' : 'visible',
    }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <DropZone isDragging={isDragging} isValid={isValidDrag}>
        <Typography
          variant="h6"
          color={isValidDrag ? "primary" : "error"}
        >
          {isValidDrag
            ? t('dropzone.dropHere')
            : t('dropzone.invalidFiles')}
        </Typography>
      </DropZone>

      {isPlaybackActive && !isTranscribing && (
        <PlaybackControls
          isPlaying={playbackData.isPlaying}
          duration={playbackData.duration}
          currentTime={playbackData.currentTime}
          onPause={pausePlayback}
          onResume={resumePlayback}
          onStop={stopPlayback}
          onSeek={seekPlayback}
        />
      )}

      <Grid container spacing={1} sx={{ height: '100%' }}>
        {/* Engine and service selection */}
        <Grid
          item
          xs={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <FeatureSelects
            selectedEngine={selectedEngine}
            onEngineChange={onEngineChange}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            isDisabled={isDisabled}
          />
        </Grid>
        {/* Text input area */}
        <Grid item xs={8} sx={{ height: '100%', padding: 0 }}>
          <Item sx={{ height: '100%', padding: 0, position: 'relative' }} >
            {/* Recording indicator */}
            {isRecording && <RecordingIndicator />}
            <TextField
              data-testid="chat-input"
              inputRef={inputRef}
              variant="outlined"
              multiline
              fullWidth
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              disabled={isDisabled}
              placeholder={!isMobile ? (settings.ctrlEnterToSend ?
                t('input.placeholderCtrlEnter') :
                t('input.placeholderEnter')) :
                ''}
              sx={{
                height: '100%',
                '& .MuiInputBase-root': {
                  color: theme.palette.text.primary,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.action.hover,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input': {
                  flexGrow: 1,
                  overflow: 'auto !important',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.background.default,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.grey[400],
                    borderRadius: '3px',
                    '&:hover': {
                      background: theme.palette.grey[500],
                    },
                  },
                  '&:hover': {
                    '&::-webkit-scrollbar-thumb': {
                      background: theme.palette.grey[500],
                    },
                  },
                },
              }}
              InputProps={{
                style: {
                  height: '100%',
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                },
              }}
            />
            {/* Loading indicator */}
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Item>
        </Grid>
        {/* Action buttons */}
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', marginTop: 1 }}>
          <ActionButtons
            onSend={handleSend}
            onUpload={onUpload}
            setInputValue={setInputValue}
            isDisabled={isDisabled}
            isAuthenticated={isAuthenticated} />
        </Grid>
      </Grid>
    </Item >
  );
};

export default InputArea;